/**
 * ESTABLISHMENT-IMPORT-DOCUMENT-MODAL
 * Import an existing unique document into the establishment
 */
import Modal from "@/components/modal"
import { useAppSelector } from "@/core/root-reducer"
import { downloadUrl } from "@/entities/document/utils/document-file.utils"
import { normalizeRiskAttributes } from "@/redux/account.types"
import LoadingModal from "@/modals/loading.modal"
import ProgressBar from "@/components/progress-bar"
import fileToImage from "@/utils/file-to-image"
import UploadButtonWidget from "@/widgets/upload-button.widget"
import { useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import { AssistantCancel } from "@/utils/assistant.utils"
import { RiskFamily } from "@/entities/risk-family/model/risk-family.types"
import {
  addActionPlan,
  getActionPlanPreprompt,
  getActionPlanPrompt,
  getDuerpReadingPreprompt,
  readInBatches,
  readWorkbook,
  toRawDocument
} from "../../model/duerp-reading.utils"
import {
  getLayoutPreprompt,
  getLayoutPrompt,
  layoutsToRawDocument,
  readGrids,
  SheetGrid
} from "../../model/duerp-layout.utils"
import { applyMapping, getGaps, getInitialMapping, hasGaps, ImportedDocument, RawDocument } from "../../model/duerp-mapping.utils"
import EstablishmentImportReviewModal from "./EstablishmentImportReviewModal"
import EstablishmentImportSettingsModal from "./EstablishmentImportSettingsModal"
import Link from "@/components/link"

interface Props {
  onClose: () => void
}

export default function EstablishmentImportDocumentModal({ onClose }: Props) {
  const settings = useAppSelector((state) => state._session.accountRisksSettings)
  const riskAttributes = normalizeRiskAttributes(settings.riskAttributes)
  const riskFamilies: RiskFamily[] = useAppSelector((state) => state.riskFamily.list)
  //Lecture en cours, partie par partie
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  //Lecture en cours, coupee si la modale se ferme
  const cancelReading = useRef<AssistantCancel | null>(null)
  //Lecture brute (du document ou d'un .json), puis rapprochee des parametres du compte pour la relecture
  const [raw, setRaw] = useState<RawDocument | null>(null)
  const [result, setResult] = useState<ImportedDocument | null>(null)

  useEffect(() => () => cancelReading.current?.(), [])

  //Parametres du document tous dans le compte : la relecture s'ouvre directement
  function openRaw(document: RawDocument) {
    setRaw(document)
    if (hasGaps(getGaps(document, riskFamilies, riskAttributes))) return
    const mapping = getInitialMapping(document, settings, riskFamilies, riskAttributes)
    setResult(applyMapping(document, mapping, settings, riskAttributes))
  }

  //Lecture ligne a ligne par l'assistant (PDF, ou Excel dont la structure n'a rien donne)
  async function readLines(parts: Parameters<typeof readInBatches>[0], actionPlanBatches: string[]) {
    const total = parts.length + actionPlanBatches.length
    setProgress({ done: 0, total })
    //1re passe : les risques des onglets d'unites (ou du PDF)
    const reading = readInBatches(parts, getDuerpReadingPreprompt(), (done) => setProgress({ done, total }))
    cancelReading.current = reading.cancel
    const responses = await reading.promise
    let document = toRawDocument(responses)

    //2e passe : le plan d'actions complete les risques lus (delais, couts, dates de realisation)
    let actionPlanResponses: any[] = []
    if (actionPlanBatches.length && document.risks.length) {
      const actionPlan = readInBatches(
        actionPlanBatches.map((batch) => ({ prompt: getActionPlanPrompt(document, batch) })),
        getActionPlanPreprompt(),
        (done) => setProgress({ done: parts.length + done, total })
      )
      cancelReading.current = actionPlan.cancel
      actionPlanResponses = await actionPlan.promise
      document = addActionPlan(document, actionPlanResponses)
    }
    return { document, responses: [...responses, ...actionPlanResponses] }
  }

  //Excel : l'assistant decrit chaque onglet, le code en lit toutes les lignes
  async function readLayouts(grids: SheetGrid[]) {
    setProgress({ done: 0, total: grids.length })
    const names = grids.map((grid) => grid.name)
    const reading = readInBatches(
      grids.map((grid) => ({ prompt: getLayoutPrompt(grid, names) })),
      getLayoutPreprompt(),
      (done) => setProgress({ done, total: grids.length })
    )
    cancelReading.current = reading.cancel
    const responses = await reading.promise
    return { document: layoutsToRawDocument(grids, responses), responses }
  }

  async function handleUpload(files: File[]) {
    const file = files[0]

    //Lecture deja faite et telechargee depuis la relecture : on la reprend sans rappeler l'assistant
    if (file.name.endsWith(".json")) return openRaw({ scales: {}, ...JSON.parse(await file.text()) })

    const isPdf = /\.pdf$/i.test(file.name)
    //Excel illisible : rien a envoyer, on reste sur la zone de depot
    const grids = isPdf ? null : await readGrids(file).catch(() => null)
    if (!isPdf && !grids) return toast.error("Je n'arrive pas à ouvrir ce fichier Excel.")
    const start = Date.now()
    //Le chargement reste affiche jusqu'a la relecture, quelle que soit l'issue
    try {
      let reading = grids ? await readLayouts(grids) : null
      //Structure illisible ou sans risque : lecture ligne a ligne, comme un PDF
      if (!reading?.document.risks.length) {
        const workbook = grids ? readWorkbook(grids) : null
        reading = await readLines(
          workbook
            ? workbook.prompts.map((prompt) => ({ prompt }))
            : [
                {
                  prompt: "Lis le document unique joint.",
                  file: { filename: file.name, fileData: await fileToImage(file) }
                }
              ],
          workbook?.actionPlanBatches ?? []
        )
      }
      const { document, responses } = reading
      console.info(`[duerp] lecture complete : ${((Date.now() - start) / 1000).toFixed(1)} s`)

      //Partie illisible ou service indisponible : les autres parties sont gardees, l'utilisateur est prevenu
      if (responses.every((response) => !response))
        return toast.error("Je n'ai pas réussi à lire votre document. Vous pouvez réessayer.")
      const failedCount = responses.filter((response) => !response).length
      if (failedCount)
        toast.warning(`${failedCount} parties du document n'ont pas pu être lues : vérifiez la relecture.`)
      if (!document.risks.length) return toast.error("Je n'ai trouvé aucun risque dans ce document.")
      openRaw(document)
    } finally {
      setProgress(null)
    }
  }

  //Modele au format de la lecture brute, avec les attributs et le barème du compte
  function downloadTemplate() {
    const template: RawDocument = {
      units: [{ id: "unite-1", name: "Accueil", map: "Bâtiment 1" }],
      risks: [
        {
          family: "Risques psychosociaux",
          RiskFamilyId: null,
          attributes: Object.fromEntries(riskAttributes.map((attribute) => [attribute.name, ""])),
          severity: 1,
          frequency: 1,
          prevention: 1,
          units: ["unite-1"],
          preventionActions: [
            {
              name: "Formation à la gestion du stress",
              description: "",
              status: "todo",
              dateStarted: null,
              datePlanned: null,
              dateDone: null,
              cost: null,
              financedBy: null
            }
          ]
        }
      ],
      scales: {}
    }
    downloadUrl(
      URL.createObjectURL(new Blob([JSON.stringify(template, null, 2)], { type: "application/json" })),
      "modele-document-unique.json"
    )
  }

  if (raw && result) {
    return <EstablishmentImportReviewModal raw={raw} result={result} onClose={onClose} />
  }

  if (raw) {
    return <EstablishmentImportSettingsModal raw={raw} onDone={setResult} onClose={onClose} />
  }

  if (progress) {
    const percent = Math.round((progress.done / progress.total) * 100)
    return (
      <LoadingModal owlAssistantText="Je lis votre document. Cela peut prendre quelques minutes.">
        <div className="flex items-center gap-10">
          <div style={{ width: 160 }}>
            <ProgressBar isCountHidden items={[{ value: progress.done }]} max={progress.total} />
          </div>
          {`${percent} %`}
        </div>
      </LoadingModal>
    )
  }

  return (
    <Modal title="Importer mon document unique" isCloseButtonVisible onClose={onClose}>
      <div>
        Déposez votre document unique au format <b>Excel</b> ou <b>PDF</b> : les unités de travail, les risques et les
        mesures de prévention en sont extraits automatiquement.
      </div>
      <div className="my-20 text-center text-lg">OU</div>
      <div className="mb-10">
        Déposez vos données au format <b>JSON</b> : elles sont reprises telles quelles, sans extraction.
      </div>
      <Link isWithoutMargin onClick={downloadTemplate}>
        Télécharger un modèle
      </Link>
      <div className="h-20" />
      <UploadButtonWidget
        showFrameStyle
        acceptedFiles=".pdf,.xls,.xlsx,.json"
        maxSize={20000000}
        onUpload={handleUpload}
      />
    </Modal>
  )
}
